import { mat4 } from 'gl-matrix';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { DoseId } from '~/domain/changes/ids';
import { DoseSummationType } from '~/domain/dicom/dicomDose';
import { DoseInputError, validatePhysicalGy } from '~/domain/dose/doseFactor';
import { buildDerivedDose, isDerivedDose } from '~/domain/dose/derivedDose';
import { doseFrameOfReferenceUid } from '~/domain/dose/doseFrame';
import { doseOutsideGrid } from '~/domain/dose/doseOutsideGrid';
import { mapDoseGy } from '~/domain/dose/mapDose';
import { scaledDoseGy } from '~/domain/dose/scaleDose';
import { doseSumGy } from '~/domain/dose/sumDoses';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { StructureSet } from '~/domain/patient/structureset';
import { rigidVolumeMapping } from '~/domain/services/resampleVolume';
import { useDomainVersion } from '~/hooks/domain/useDomainVersion';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { replanningMappedDosesState } from '~/state/atoms/adaptiveReplanningAtoms';
import { doseDisplaySettingsState } from '~/state/atoms/dose/doseDisplaySettings';
import { generateUID } from '~/utils/dicomUid';
import { useReviewDatasets } from './useReviewDatasets';
import { logErrorName } from '~/utils/logErrorName';
import { doseLabel } from '~/domain/projections/factories';

export const DOSE_SUM_VERSION = 'Workspace+ dose sum 4';

export interface CurrentDoseCandidate {
    readonly id: DoseId;
    readonly label: string;
    readonly dose: DoseMatrix;
    // The viewer's display rescale of the dose in percent; the sum uses the dose as measured.
    readonly displayPercent: number;
}

export type DoseSumState =
    | { readonly status: 'idle' }
    | { readonly status: 'summing' }
    | { readonly status: 'created'; readonly label: string }
    | { readonly status: 'failed'; readonly messageId: string };

export interface DoseSum {
    // The prior dose as it is shown on the target.
    readonly priorDose?: DoseMatrix;
    readonly priorDisplayPercent: number;
    // Doses planned on the target scan itself.
    readonly currentDoses: readonly CurrentDoseCandidate[];
    readonly blockedReasonId?: string;
    readonly state: DoseSumState;
    readonly create: (
        currentDoseId: DoseId,
        priorWeight: number,
        currentWeight: number
    ) => Promise<void>;
    readonly reset: () => void;
}

// Prior dose as shown on the target plus the current dose, on the prior dose's grid, created as a new dose.
export function useDoseSum(
    priorOnTarget: DoseMatrix | undefined,
    targetStructures: StructureSet | undefined
): DoseSum {
    const intl = useIntl();
    const patient = useSelectedPatient();
    // Re-rendered per changeset: the current doses are the patient's, added in place.
    useDomainVersion();
    const session = useSessionOrNull();
    const { runDose, priorDose, propagatedSet } = useReviewDatasets();
    const { setEnvironmentDose } = useViewportEnvironment();
    const [state, setState] = useState<DoseSumState>({ status: 'idle' });
    const primarySeriesUid = useSelectedPrimaryImage()?.seriesUid;
    const mappedDoses = useRecoilValue(replanningMappedDosesState);
    const displaySettings = useRecoilValue(doseDisplaySettingsState);
    const displayPercentOf = (dose: DoseMatrix) =>
        (displaySettings.get(dose.id)?.doseScale ?? 1) * 100;

    const targetImage = propagatedSet?.image ?? undefined;
    const targetFoR = targetImage?.frameOfReferenceUid;
    const current = (patient?.doses ?? []).filter(
        (dose) =>
            dose !== runDose &&
            // The prior itself is the carried dose; offering it would sum the prior with itself.
            dose !== priorDose &&
            dose !== priorOnTarget &&
            !isDerivedDose(dose) &&
            !!targetFoR &&
            doseFrameOfReferenceUid(dose) === targetFoR
    );
    const currentDoses = current.map((dose) => ({
        id: dose.id,
        label:
            doseLabel(dose) ||
            intl.formatMessage({ id: 'replanning.doseSum.unnamedDose' }),
        dose,
        displayPercent: displayPercentOf(dose),
    }));

    const blockedReasonId = !priorOnTarget
        ? 'replanning.doseSum.blocked.noPrior'
        : currentDoses.length === 0
          ? 'replanning.doseSum.blocked.noCurrent'
          : undefined;

    const create = useCallback(
        async (
            currentDoseId: DoseId,
            priorWeight: number,
            currentWeight: number
        ) => {
            setState({ status: 'summing' });
            try {
                const currentDose = patient?.doses.find(
                    (dose) => dose.id === currentDoseId
                );
                if (
                    !session ||
                    !patient ||
                    !priorOnTarget ||
                    !targetStructures ||
                    !targetFoR ||
                    !currentDose
                ) {
                    throw new DoseInputError(
                        'A dose chosen for the sum is no longer loaded.',
                        'replanning.doseSum.error.missing'
                    );
                }
                if (doseFrameOfReferenceUid(priorOnTarget) !== targetFoR) {
                    throw new DoseInputError(
                        'The prior dose is not in the target scan frame.',
                        'replanning.doseSum.error.frame'
                    );
                }
                validatePhysicalGy(priorOnTarget);
                validatePhysicalGy(currentDose);
                const grid = priorOnTarget.grid;
                // Current dose beyond the prior dose grid is not part of the sum; it is counted and recorded.
                const dropped = await doseOutsideGrid(
                    currentDose.realPixels,
                    currentDose.grid,
                    grid,
                    rigidVolumeMapping(mat4.create())
                );
                // Both doses are in the target scan's frame: the current dose is resampled onto the grid, each weighted.
                const resampled = await mapDoseGy(
                    currentDose.realPixels,
                    currentDose.grid,
                    grid,
                    rigidVolumeMapping(mat4.create()),
                    currentWeight
                );
                if (!resampled || !dropped) {
                    throw new Error('Resampling cancelled');
                }
                const priorMapping = mappedDoses.find(
                    (record) => record.doseId === priorOnTarget.id
                );
                const doseGy = doseSumGy([
                    scaledDoseGy(priorOnTarget.realPixels, priorWeight),
                    resampled.doseGy,
                ]);
                const label = intl.formatMessage({
                    id: 'replanning.doseSum.label',
                });
                const dose = buildDerivedDose({
                    sopInstanceUid: generateUID(),
                    frameOfReferenceUid: targetFoR,
                    grid,
                    doseGy,
                    description: label,
                    comment:
                        `Dose sum: ${priorWeight} × "${doseLabel(priorOnTarget)}" + ${currentWeight} × "${doseLabel(currentDose)}". ` +
                        `The current dose resampled trilinearly onto the prior dose grid ` +
                        `${grid.sizeX}x${grid.sizeY}x${grid.sizeZ} at ${grid.resX}x${grid.resY}x${grid.resZ} mm; ` +
                        `0 Gy outside its grid (${resampled.outsideSourceVoxels} voxels). ` +
                        `Current dose outside the prior dose grid, not summed: ${dropped.outsideVoxels} of ` +
                        `${dropped.plannedVoxels} voxels above 0 Gy, at most ${dropped.maxOutsideGy.toFixed(2)} Gy as measured. ` +
                        (priorMapping
                            ? `Prior dose on the target: ${priorMapping.unmappedVoxels} voxels not reached by the transform ` +
                              `and ${priorMapping.outsideSourceVoxels} outside its own grid, 0 Gy. `
                            : '') +
                        `${DOSE_SUM_VERSION}.`,
                    summationType: DoseSummationType.MULTI_PLAN,
                    softwareVersion: DOSE_SUM_VERSION,
                    referencedStructureSetUid: targetStructures.dicomUid,
                });
                session.doseService.add(dose, targetStructures);
                // Drawn only over the target scan; otherwise it waits in the dose list.
                if (primarySeriesUid === targetImage?.seriesUid) {
                    setEnvironmentDose([dose], patient.dataSet.PatientID ?? '');
                }
                setState({ status: 'created', label });
            } catch (error) {
                logErrorName('Dose sum failed:', error);
                setState({
                    status: 'failed',
                    messageId:
                        error instanceof DoseInputError
                            ? error.messageId
                            : 'replanning.doseSum.error.failed',
                });
            }
        },
        [
            session,
            patient,
            priorOnTarget,
            targetStructures,
            targetFoR,
            intl,
            setEnvironmentDose,
            primarySeriesUid,
            targetImage,
            mappedDoses,
        ]
    );

    const reset = useCallback(() => setState({ status: 'idle' }), []);

    return {
        priorDose: priorOnTarget,
        priorDisplayPercent: priorOnTarget
            ? displayPercentOf(priorOnTarget)
            : 100,
        currentDoses,
        blockedReasonId,
        state,
        create,
        reset,
    };
}
